import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ShieldCheck, Plus, PencilSimple, Trash, Lock, CaretDown, CaretRight, Check } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RolesPage = () => {
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState([]);  // [{module, label, actions:[{key,label}]}]
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', permissions: {} });
    const [expandedModules, setExpandedModules] = useState({});

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        try {
            const [rolesRes, metaRes] = await Promise.all([
                axios.get(`${API}/roles`, { headers: authHeaders() }),
                axios.get(`${API}/roles/resources`, { headers: authHeaders() })
            ]);
            setRoles(rolesRes.data);
            setCatalog(metaRes.data.catalog || []);
        } catch (e) {
            toast.error('Error al cargar roles');
        }
    };

    const openCreate = () => {
        setEditing(null);
        setFormData({ name: '', description: '', permissions: {} });
        // Expand first 3 modules by default for easier scanning
        const exp = {};
        (catalog || []).slice(0, 3).forEach(m => { exp[m.module] = true; });
        setExpandedModules(exp);
        setShowModal(true);
    };

    const openEdit = (role) => {
        if (role.is_system) {
            toast.error('Los roles del sistema no pueden modificarse');
            return;
        }
        setEditing(role);
        setFormData({ name: role.name, description: role.description || '', permissions: role.permissions || {} });
        // Auto-expand modules where the role already has permissions
        const exp = {};
        Object.keys(role.permissions || {}).forEach(k => { exp[k] = true; });
        setExpandedModules(exp);
        setShowModal(true);
    };

    const togglePermission = (module_, action) => {
        setFormData((prev) => {
            const current = prev.permissions[module_] || [];
            const has = current.includes(action);
            const next = has ? current.filter(a => a !== action) : [...current, action];
            const newPerms = { ...prev.permissions };
            if (next.length) newPerms[module_] = next;
            else delete newPerms[module_];
            return { ...prev, permissions: newPerms };
        });
    };

    const toggleAllForModule = (module_, allActions, value) => {
        setFormData((prev) => {
            const newPerms = { ...prev.permissions };
            if (value) newPerms[module_] = allActions.map(a => a.key);
            else delete newPerms[module_];
            return { ...prev, permissions: newPerms };
        });
    };

    const toggleAllPermissions = (value) => {
        if (value) {
            const all = {};
            catalog.forEach(m => { all[m.module] = m.actions.map(a => a.key); });
            setFormData(p => ({ ...p, permissions: all }));
        } else {
            setFormData(p => ({ ...p, permissions: {} }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                permissions: formData.permissions,
            };
            if (editing) {
                await axios.put(`${API}/roles/${editing.id}`, payload, { headers: authHeaders() });
                toast.success('Rol actualizado');
            } else {
                await axios.post(`${API}/roles`, payload, { headers: authHeaders() });
                toast.success('Rol creado');
            }
            setShowModal(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar rol');
        }
    };

    const handleDelete = async (role) => {
        if (role.is_system) return toast.error('Rol del sistema no se puede eliminar');
        if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;
        try {
            await axios.delete(`${API}/roles/${role.id}`, { headers: authHeaders() });
            toast.success('Rol eliminado');
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const totalPermissions = catalog.reduce((sum, m) => sum + m.actions.length, 0);
    const grantedCount = Object.values(formData.permissions).reduce((s, a) => s + (a || []).length, 0);

    return (
        <div className="space-y-6 animate-fade-up max-w-[1400px]" data-testid="roles-page">
            <div className="flex items-end justify-between pb-6 border-b border-[#ECE3D2]">
                <div>
                    <div className="flex items-center gap-2 text-xs mb-2">
                        <span className="text-[#8B8474]">Configuración</span>
                        <span className="text-[#C7BCA5]">/</span>
                        <span className="text-[#1F2D4B] font-medium">Roles y Permisos</span>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-[#1F2D4B]">Roles y Permisos</h1>
                    <p className="text-sm text-[#4B5878] mt-1.5">
                        {roles.length} {roles.length === 1 ? 'rol configurado' : 'roles configurados'} · {totalPermissions} permisos granulares disponibles
                    </p>
                </div>
                <Button onClick={openCreate} data-testid="create-role-btn" className="h-10 px-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium btn-lift">
                    <Plus size={16} weight="bold" className="mr-1.5" /> Nuevo rol
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {roles.map((role) => {
                    const moduleCount = Object.keys(role.permissions || {}).length;
                    const permCount = Object.values(role.permissions || {}).reduce((s, a) => s + (a || []).length, 0);
                    return (
                        <div key={role.id} className="bg-white border border-[#ECE3D2] rounded-lg p-5" data-testid={`role-card-${role.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-start gap-2.5">
                                    <div className="w-9 h-9 rounded-md bg-[#1F2D4B] text-white flex items-center justify-center">
                                        <ShieldCheck size={16} weight="duotone" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-[#1F2D4B]">{role.name}</h3>
                                            {role.is_system && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FAF6F0] text-[#8B8474] px-1.5 py-0.5 rounded flex items-center gap-1">
                                                    <Lock size={9} weight="bold" /> Sistema
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-[#8B8474] mt-0.5 leading-snug">{role.description}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="text-[11px] text-[#4B5878] mb-3 flex items-center gap-3">
                                <span><span className="font-semibold text-[#1F2D4B]">{moduleCount}</span> módulos</span>
                                <span className="text-[#C7BCA5]">·</span>
                                <span><span className="font-semibold text-[#1F2D4B]">{permCount}</span> permisos</span>
                            </div>

                            <div className="flex gap-2 pt-3 border-t border-[#ECE3D2]">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEdit(role)}
                                    disabled={role.is_system}
                                    data-testid={`edit-role-${role.id}`}
                                    className="flex-1 h-8 text-xs rounded-md border-[#ECE3D2] disabled:opacity-40"
                                >
                                    <PencilSimple size={12} className="mr-1" /> Editar
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(role)}
                                    disabled={role.is_system}
                                    data-testid={`delete-role-${role.id}`}
                                    className="h-8 px-2 text-xs rounded-md border-[#ECE3D2] text-red-600 hover:bg-red-50 disabled:opacity-40"
                                >
                                    <Trash size={12} />
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal — Role builder with permission catalog by module */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="!block sm:max-w-4xl !max-h-[95vh] !w-[95vw] !p-0 !gap-0 bg-white border border-[#ECE3D2] rounded-xl overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b border-[#ECE3D2]">
                        <DialogTitle className="font-display text-2xl font-bold text-[#1F2D4B]">
                            {editing ? 'Editar rol' : 'Nuevo rol'}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5878]">
                            Activa los permisos granulares que tendrá este rol en cada módulo
                        </DialogDescription>
                    </DialogHeader>

                    <form id="role-form" onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(95vh-220px)]">
                        <div className="p-6 pb-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Nombre del rol *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Ej. Supervisor de sucursal"
                                        required
                                        data-testid="role-name-input"
                                        className="h-10 rounded-md border-[#ECE3D2]"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-[#1F2D4B] mb-1.5 block">Descripción</Label>
                                    <Input
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Breve descripción"
                                        data-testid="role-description-input"
                                        className="h-10 rounded-md border-[#ECE3D2]"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between bg-[#F8F4EC] border border-[#ECE3D2] rounded-md px-4 py-2.5 mb-4">
                                <div className="text-xs text-[#4B5878]">
                                    <span className="font-bold text-[#1F2D4B]">{grantedCount}</span> / {totalPermissions} permisos otorgados
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleAllPermissions(true)}
                                        className="text-[11px] font-semibold text-[#1F2D4B] hover:underline"
                                        data-testid="select-all-perms"
                                    >
                                        Marcar todo
                                    </button>
                                    <span className="text-[#C7BCA5]">·</span>
                                    <button
                                        type="button"
                                        onClick={() => toggleAllPermissions(false)}
                                        className="text-[11px] font-semibold text-red-700 hover:underline"
                                        data-testid="clear-all-perms"
                                    >
                                        Limpiar todo
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6 space-y-2">
                            {catalog.map((m) => {
                                const expanded = !!expandedModules[m.module];
                                const granted = formData.permissions[m.module] || [];
                                const allGranted = m.actions.every(a => granted.includes(a.key));
                                const noneGranted = granted.length === 0;
                                return (
                                    <div key={m.module} className="border border-[#ECE3D2] rounded-md overflow-hidden">
                                        <div
                                            className="flex items-center justify-between px-4 py-2.5 bg-[#FAF6F0] cursor-pointer"
                                            onClick={() => setExpandedModules(prev => ({ ...prev, [m.module]: !prev[m.module] }))}
                                        >
                                            <div className="flex items-center gap-2">
                                                {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                                                <span className="font-semibold text-[#1F2D4B] text-sm">{m.label}</span>
                                                {!noneGranted && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1F2D4B] text-white px-1.5 py-0.5 rounded">
                                                        {granted.length}/{m.actions.length}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={allGranted}
                                                    onCheckedChange={(v) => toggleAllForModule(m.module, m.actions, !!v)}
                                                    data-testid={`module-toggle-${m.module}`}
                                                    className="data-[state=checked]:bg-[#1F2D4B] data-[state=checked]:border-[#1F2D4B]"
                                                />
                                                <span className="text-[11px] text-[#8B8474]">Todo</span>
                                            </div>
                                        </div>
                                        {expanded && (
                                            <div className="bg-white px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                {m.actions.map((a) => {
                                                    const checked = granted.includes(a.key);
                                                    return (
                                                        <label
                                                            key={a.key}
                                                            className={`flex items-center gap-2.5 p-2 rounded border text-xs cursor-pointer transition-colors ${
                                                                checked ? 'border-[#1F2D4B] bg-[#F8F4EC]' : 'border-[#ECE3D2] hover:bg-[#FAFAFA]'
                                                            }`}
                                                            data-testid={`perm-${m.module}-${a.key}`}
                                                        >
                                                            <Checkbox
                                                                checked={checked}
                                                                onCheckedChange={() => togglePermission(m.module, a.key)}
                                                                className="data-[state=checked]:bg-[#1F2D4B] data-[state=checked]:border-[#1F2D4B]"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="font-medium text-[#1F2D4B]">{a.label}</div>
                                                                <div className="text-[10px] text-[#8B8474]">{m.module}.{a.key}</div>
                                                            </div>
                                                            {checked && <Check size={12} weight="bold" className="text-[#1F2D4B]" />}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </form>

                    <div className="flex gap-2 p-6 pt-4 border-t border-[#ECE3D2] bg-white">
                        <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1 h-10 rounded-md border-[#ECE3D2] text-[#1F2D4B] text-sm font-medium">Cancelar</Button>
                        <Button type="submit" form="role-form" data-testid="save-role-btn" className="flex-1 h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm font-medium btn-lift">
                            {editing ? 'Guardar cambios' : 'Crear rol'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RolesPage;
